/**
 * Meal Plan Sync Service
 *
 * Handles synchronization of meal plan data to/from Firestore.
 * Follows the same pattern as syncService.ts for inventory.
 */

import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  collection,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { PlannedMeal, MealType } from '../redux/mealPlanSlice';
import { resolveDataScope, buildScopedPath } from './householdPaths';
import { removeUndefined } from './groceryListSyncService';

/**
 * Firestore document structure for a meal plan week
 */
export interface FirestoreMealPlanWeek {
  meals: FirestorePlannedMeal[];
  updatedAt: Timestamp | string;
  userId: string;
}

/**
 * Stripped down meal for Firestore storage (no denormalized recipe data)
 */
export interface FirestorePlannedMeal {
  id: string;
  recipeId: string;
  scheduledFor: string;
  dayOfWeek: number;
  mealType: MealType;
  servings: number;
  notes?: string;
  createdAt: string;
  ingredientsReviewed: boolean;
  missingIngredients: string[];
  dietaryRole?: string;
  freeText?: string;
  status?: 'planned' | 'cooked';
  cookedAt?: string;
  rating?: number; // per-meal star rating (1-5)
  cookNote?: string; // free-form "note for next time" captured with the cook
  cookNoteBy?: string; // uid of whoever wrote cookNote — the plan is shared
  approvals?: Record<string, { value: 'yes' | 'no'; at: string; onRecipeId: string }>;
  cookId?: string; // uid of the household member cooking this meal
  createdByUid?: string; // uid of proposal author; required for removal rights — dropping it silently revokes author control
  mode?: 'proposed' | 'accepted'; // meal proposal status; absent = accepted
  proposesInsteadOf?: string; // id of the meal this proposal replaces; load-bearing for acceptMeal to remove the incumbent
}

/**
 * Result of fetching a meal plan week from Firestore
 */
export interface MealPlanWeekResult {
  meals: FirestorePlannedMeal[];
  updatedAt: string;
}

/**
 * Get the meal plan collection for the active scope (household when paired,
 * else the user's own).
 */
const getMealPlanCollection = (
  userId: string,
  householdId?: string | null
) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope: missing userId and householdId');
  }
  const [root, id, ...rest] = buildScopedPath(scope, 'mealPlan');
  return collection(firestore, root, id, ...rest);
};

/**
 * Get reference to a specific week document under the active scope.
 */
const getWeekDocRef = (
  userId: string,
  weekStartDate: string,
  householdId?: string | null
) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope: missing userId and householdId');
  }
  const [root, id, ...rest] = buildScopedPath(
    scope,
    'mealPlan',
    weekStartDate
  );
  return doc(firestore, root, id, ...rest);
};

/**
 * Strip recipe data from meals to reduce storage size.
 * Recipe data can be fetched separately when needed.
 */
const stripMealForStorage = (meal: PlannedMeal): FirestorePlannedMeal => {
  const { recipe, matchedDeals, matchMetadata, ...strippedMeal } = meal;
  return strippedMeal;
};

/**
 * Sync a meal plan week to Firestore
 *
 * @param userId - The user's ID
 * @param weekStartDate - The Monday start date of the week (YYYY-MM-DD)
 * @param meals - Array of planned meals for the week
 */
export const syncMealPlanWeekToCloud = async (
  userId: string,
  weekStartDate: string,
  meals: PlannedMeal[],
  householdId?: string | null
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const docRef = getWeekDocRef(userId, weekStartDate, householdId);

  // Strip recipe data to reduce storage size
  const strippedMeals = meals.map(stripMealForStorage);

  const weekData: FirestoreMealPlanWeek = {
    meals: strippedMeals,
    updatedAt: serverTimestamp() as Timestamp,
    userId,
  };

  await setDoc(docRef, removeUndefined(weekData), { merge: true });
  console.log(`✅ Synced meal plan week ${weekStartDate} to cloud`);
};

/**
 * Fetch a single meal plan week from Firestore
 *
 * @param userId - The user's ID
 * @param weekStartDate - The Monday start date of the week (YYYY-MM-DD)
 * @returns The meal plan week data, or null if not found
 */
export const fetchMealPlanWeekFromCloud = async (
  userId: string,
  weekStartDate: string,
  householdId?: string | null
): Promise<MealPlanWeekResult | null> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const docRef = getWeekDocRef(userId, weekStartDate, householdId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data() as FirestoreMealPlanWeek;
  return {
    meals: data.meals,
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : data.updatedAt?.toDate?.()?.toISOString() || '',
  };
};

/**
 * Fetch all meal plan weeks for a user from Firestore
 *
 * @param userId - The user's ID
 * @returns Record of weekStartDate -> MealPlanWeekResult
 */
export const fetchAllMealPlanWeeksFromCloud = async (
  userId: string,
  householdId?: string | null
): Promise<Record<string, MealPlanWeekResult>> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const collectionRef = getMealPlanCollection(userId, householdId);
  const snapshot = await getDocs(collectionRef);

  const result: Record<string, MealPlanWeekResult> = {};

  snapshot.docs.forEach((doc) => {
    const data = doc.data() as FirestoreMealPlanWeek;
    result[doc.id] = {
      meals: data.meals,
      updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : data.updatedAt?.toDate?.()?.toISOString() || '',
    };
  });

  console.log(`✅ Fetched ${Object.keys(result).length} meal plan weeks from cloud`);
  return result;
};

/**
 * Delete a meal plan week from Firestore
 *
 * @param userId - The user's ID
 * @param weekStartDate - The Monday start date of the week (YYYY-MM-DD)
 */
/**
 * Live-subscribe to all meal plan weeks under the active scope. Returns
 * the full Record<weekStartDate, MealPlanWeekResult> on every snapshot.
 */
export const subscribeToMealPlanWeeks = (
  userId: string,
  householdId: string | null | undefined,
  onUpdate: (weeks: Record<string, MealPlanWeekResult>) => void,
  onError?: (error: Error) => void
): (() => void) => {
  if (!isFirebaseEnabled() || !firestore) {
    return () => {};
  }
  const collectionRef = getMealPlanCollection(userId, householdId);
  return onSnapshot(
    collectionRef,
    (snapshot) => {
      const result: Record<string, MealPlanWeekResult> = {};
      snapshot.docs.forEach((d) => {
        const data = d.data() as FirestoreMealPlanWeek;
        result[d.id] = {
          meals: data.meals,
          updatedAt:
            typeof data.updatedAt === 'string'
              ? data.updatedAt
              : data.updatedAt?.toDate?.()?.toISOString() || '',
        };
      });
      onUpdate(result);
    },
    (error) => {
      console.error('Meal plan subscription error:', error);
      onError?.(error);
    }
  );
};

export const deleteMealPlanWeekFromCloud = async (
  userId: string,
  weekStartDate: string,
  householdId?: string | null
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const docRef = getWeekDocRef(userId, weekStartDate, householdId);
  await deleteDoc(docRef);
  console.log(`✅ Deleted meal plan week ${weekStartDate} from cloud`);
};

